import { Component, OnInit, Input, SimpleChanges, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { ITypesSelection } from 'src/app/core/interfaces/types-selection.interface';
import { TexteService } from 'src/app/core/services/texte.service';
import { ApiService, FileParameter, TypeFile, RefDto, PathologieDto } from 'src/app/core/services/api.service';
import { FormGroup, FormControl, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { IDemande } from 'src/app/core/interfaces/demande.interface';
import { forkJoin, Observable, of } from 'rxjs';
import { mergeMap, catchError } from 'rxjs/operators';
import { ToolboxService } from 'src/app/core/services/toolbox.service';

@Component({
    selector: 'app-demande-form',
    templateUrl: './demande-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DemandeFormComponent implements OnInit, OnChanges {

  @Input() typesSelected: ITypesSelection;

  public demandeForm: FormGroup;
  public specialite: RefDto;
  public pathologie: PathologieDto;

  constructor(
    public texteService: TexteService,
    private apiService: ApiService,
    private toolboxService: ToolboxService
  ) { }

  specialites: RefDto[] = [];
  pathologies: PathologieDto[] = [];
  private allPathologies: PathologieDto[] = [];

  ngOnInit(): void {
    this.apiService.getSpecialites().subscribe(data => {
      this.specialites = data;
    });

    this.apiService.getPathologies().subscribe(data => {
      this.allPathologies = data;
    });

    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.typesSelected != null && !changes.typesSelected.firstChange) {
      this.buildForm();
    }
  }

  private buildForm() {
    this.demandeForm = new FormGroup({
      specialite: new FormControl('', Validators.required),
      pathologie: new FormControl(''),
      FileCRHI: new FormControl(''),
      FileCRI: new FormControl('')
    });

    if (this.typesSelected.situation.code !== 'INT') {
      this.demandeForm.addControl('FileMedecin', new FormControl(''));
      this.demandeForm.addControl('FileSanguin', new FormControl(''));
    }

    if (this.typesSelected.situation.code === 'FRA') {
      this.demandeForm.addControl('cancer', new FormControl(''));
    } else if (this.typesSelected.situation.code === 'INT') {
      this.demandeForm.addControl('information', new FormControl(''));
      this.demandeForm.addControl('FilePassport', new FormControl(''));
      if (this.typesSelected.couverture.code === 'CAP') {
        this.demandeForm.addControl('FileJustif', new FormControl(''));
      }
      if (this.typesSelected.couverture.code === 'CUE') {
        this.demandeForm.addControl('FileUE', new FormControl('', Validators.required));
      } else {
        this.demandeForm.addControl('FileUE', new FormControl(''));
      }

      this.demandeForm.addControl('FileCRM', new FormControl(''));
      this.demandeForm.addControl('FileCRO', new FormControl(''));
      this.demandeForm.addControl('FileCRHO', new FormControl(''));
      this.demandeForm.addControl('information', new FormControl(''));
    }
    this.demandeForm.setValidators(this.totalSizeValidator);
  }

  private totalSizeValidator: ValidatorFn = (): ValidationErrors | null => {
    const totalSize = this.getTotalSize();
    return totalSize <= 10000000 ? null : { totalSizeError: true };
  }

  public getTotalSize(): number {
    let totalSize = 0;

    totalSize = totalSize + (this.demandeForm.get('FileCRHI').value != null &&
      this.demandeForm.get('FileCRHI').value.size != null ? this.demandeForm.get('FileCRHI').value.size : 0);

    totalSize = totalSize + (this.demandeForm.get('FileCRI').value != null &&
      this.demandeForm.get('FileCRI').value.size != null ? this.demandeForm.get('FileCRI').value.size : 0);

    if (this.typesSelected.situation.code !== 'INT') {
      totalSize = totalSize + (this.demandeForm.get('FileMedecin').value != null &&
        this.demandeForm.get('FileMedecin').value.size != null ? this.demandeForm.get('FileMedecin').value.size : 0);

      totalSize = totalSize + (this.demandeForm.get('FileSanguin').value != null &&
        this.demandeForm.get('FileSanguin').value.size != null ? this.demandeForm.get('FileSanguin').value.size : 0);
    }

    if (this.typesSelected.situation.code === 'INT') {
      totalSize = totalSize + (this.demandeForm.get('FilePassport').value != null &&
        this.demandeForm.get('FilePassport').value.size != null ? this.demandeForm.get('FilePassport').value.size : 0);
      if (this.demandeForm.get('FileUE').value !== null) {
        totalSize = totalSize + (this.demandeForm.get('FileUE').value != null &&
          this.demandeForm.get('FileUE').value.size != null ? this.demandeForm.get('FileUE').value.size : 0);
      }

      if (this.typesSelected.couverture.code === 'CAP') {
        totalSize = totalSize + (this.demandeForm.get('FileJustif').value != null &&
          this.demandeForm.get('FileJustif').value.size != null ? this.demandeForm.get('FileJustif').value.size : 0);
      }
      totalSize = totalSize + (this.demandeForm.get('FileCRM').value != null &&
        this.demandeForm.get('FileCRM').value.size != null ? this.demandeForm.get('FileCRM').value.size : 0);

      totalSize = totalSize + (this.demandeForm.get('FileCRO').value != null &&
        this.demandeForm.get('FileCRO').value.size != null ? this.demandeForm.get('FileCRO').value.size : 0);

      totalSize = totalSize + (this.demandeForm.get('FileCRHO').value != null &&
        this.demandeForm.get('FileCRHO').value.size != null ? this.demandeForm.get('FileCRHO').value.size : 0);
    }
    return totalSize;
  }

  public isValid(): boolean {
    return this.demandeForm.valid;
  }

  onSpecialiteChanged(event) {
    this.pathologies = this.allPathologies.filter(p => p.specialite.code === event.value.code);
    if (this.pathologies.length > 0) {
      this.pathologies = this.toolboxService.arrayInsert(
        this.pathologies,
        this.allPathologies.find(p => p.code === 'AUTRE'),
        this.pathologies.length
      );
      this.demandeForm.addControl('pathologie', new FormControl('', Validators.required));
    } else {
      this.demandeForm.removeControl('pathologie');
    }
    this.demandeForm.updateValueAndValidity();
  }

  onFileChange(event, prop: string) {
    if (event == null) {
      this.demandeForm.controls[prop].setValue(null);
    } else if (!this.checkExtension(event.name)) {
      this.demandeForm.controls[prop].setErrors({ extensionError: true });
    } else {
      this.demandeForm.controls[prop].setValue(event);
    }
  }

  public getForm(): IDemande {
    return {
      specialite: this.demandeForm.get('specialite').value,
      pathologie: this.demandeForm.get('pathologie') != null ? this.demandeForm.get('pathologie').value : null,
      cancer: this.typesSelected.situation.code === 'FRA' ? this.demandeForm.get('cancer').value : null,
      information: this.typesSelected.situation.code === 'INT' ? this.demandeForm.get('information').value : null,
    } as IDemande;
  }

  public uploadFiles(idDossier): Observable<boolean> {
    const subscribeList = [];

    if (this.typesSelected.situation.code === 'INT' && this.demandeForm.get('FilePassport').value != null &&
      this.demandeForm.get('FilePassport').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FilePassport').value,
        fileName: this.demandeForm.get('FilePassport').value.name
      } as FileParameter, idDossier, TypeFile._0));
    }

    if (this.typesSelected.situation.code === 'INT' && this.demandeForm.get('FileUE').value != null &&
      this.demandeForm.get('FileUE').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileUE').value,
        fileName: this.demandeForm.get('FileUE').value.name
      } as FileParameter, idDossier, TypeFile._1));
    }

    if (this.typesSelected.situation.code === 'INT' && this.typesSelected.couverture.code === 'CAP'
      && this.demandeForm.get('FileJustif').value != null && this.demandeForm.get('FileJustif').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileJustif').value,
        fileName: this.demandeForm.get('FileJustif').value.name
      } as FileParameter, idDossier, TypeFile._2));
    }

    if (this.typesSelected.situation.code === 'INT' && this.demandeForm.get('FileCRM').value != null &&
      this.demandeForm.get('FileCRM').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileCRM').value,
        fileName: this.demandeForm.get('FileCRM').value.name
      } as FileParameter, idDossier, TypeFile._3));
    }

    if (this.typesSelected.situation.code === 'INT' && this.demandeForm.get('FileCRO').value != null &&
      this.demandeForm.get('FileCRO').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileCRO').value,
        fileName: this.demandeForm.get('FileCRO').value.name
      } as FileParameter, idDossier, TypeFile._4));
    }

    if (this.typesSelected.situation.code === 'INT' && this.demandeForm.get('FileCRHO').value != null &&
      this.demandeForm.get('FileCRHO').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileCRHO').value,
        fileName: this.demandeForm.get('FileCRHO').value.name
      } as FileParameter, idDossier, TypeFile._5));
    }

    if (this.demandeForm.get('FileCRHI').value != null &&
      this.demandeForm.get('FileCRHI').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileCRHI').value,
        fileName: this.demandeForm.get('FileCRHI').value.name
      } as FileParameter, idDossier, TypeFile._6));
    }

    if (this.demandeForm.get('FileCRI').value != null &&
      this.demandeForm.get('FileCRI').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileCRI').value,
        fileName: this.demandeForm.get('FileCRI').value.name
      } as FileParameter, idDossier, TypeFile._7));
    }

    if (this.typesSelected.situation.code !== 'INT' && this.demandeForm.get('FileMedecin').value != null &&
      this.demandeForm.get('FileMedecin').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileMedecin').value,
        fileName: this.demandeForm.get('FileMedecin').value.name
      } as FileParameter, idDossier, TypeFile._8));
    }

    if (this.typesSelected.situation.code !== 'INT' && this.demandeForm.get('FileSanguin').value != null &&
      this.demandeForm.get('FileSanguin').value !== '') {
      subscribeList.push(this.apiService.uploadFile({
        data: this.demandeForm.get('FileSanguin').value,
        fileName: this.demandeForm.get('FileSanguin').value.name
      } as FileParameter, idDossier, TypeFile._9));
    }

    if (subscribeList.length === 0) { return of(true); }

    return forkJoin(subscribeList)
      .pipe(
        mergeMap(data => {
          if (data) {
            data.forEach(response => {
              if (!response) { return of(false); }
            });
            return of(true);
          } else {
            return of(false);
          }
        }),
        catchError(() => of(null))
      );
  }

  private checkExtension(filename: string): boolean {
    const exts = ['pdf', 'jpeg', 'jpg', 'png'];
    const re = /(?:\.([^.]+))?$/;
    const ext = re.exec(filename)[1].toLowerCase();

    let result = false;
    exts.forEach(e => {
      if (e === ext) { result = true; }
    });

    return result;
  }
}
